var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { forwardRef } from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { cn } from 'root/src/client/v2/common/helpers/cn';
// Radix Accordion ported with redesign tokens. `@radix-ui/react-accordion` is a dependency, so we
// reuse it instead of hand-rolling open/close state and keyboard handling. The expand / collapse uses
// the `accordion-show` / `accordion-hide` Tailwind animations (keyed on `--radix-accordion-content-height`,
// see tailwind.config.js); `motion-reduce:animate-none` honours `prefers-reduced-motion`. General
// primitive (DRY): single or multiple, controlled or uncontrolled, reusable by any redesign page.
export const Accordion = AccordionPrimitive.Root;
export const AccordionItem = forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (<AccordionPrimitive.Item ref={ref} className={cn('overflow-hidden rounded-xl border border-solid border-border bg-card transition-colors', 'data-[state=open]:border-primary/40 hover:border-border/80 data-[state=open]:hover:border-primary/40', className)} {...props}/>);
});
AccordionItem.displayName = AccordionPrimitive.Item.displayName;
export const AccordionTrigger = forwardRef((_a, ref) => {
    var { className, children } = _a, props = __rest(_a, ["className", "children"]);
    return (<AccordionPrimitive.Header className="flex">
		<AccordionPrimitive.Trigger ref={ref} className={cn('group flex flex-1 items-center justify-between gap-4 px-5 py-4 text-left', 'font-display text-base font-semibold text-foreground transition-colors', 'hover:text-primary data-[state=open]:text-primary', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40', className)} {...props}>
			{children}
			<span aria-hidden className={cn('relative flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full', 'border border-solid border-border text-muted-foreground transition-colors', 'group-hover:border-primary/40 group-hover:text-primary', 'group-data-[state=open]:border-primary/40 group-data-[state=open]:text-primary')}>
				<AddIcon sx={{ fontSize: 18 }} className="absolute transition-opacity group-data-[state=open]:opacity-0"/>
				<RemoveIcon sx={{ fontSize: 18 }} className="absolute opacity-0 transition-opacity group-data-[state=open]:opacity-100"/>
			</span>
		</AccordionPrimitive.Trigger>
	</AccordionPrimitive.Header>);
});
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;
export const AccordionContent = forwardRef((_a, ref) => {
    var { className, children } = _a, props = __rest(_a, ["className", "children"]);
    return (<AccordionPrimitive.Content ref={ref} className={cn('overflow-hidden', 'data-[state=open]:animate-accordion-show data-[state=closed]:animate-accordion-hide motion-reduce:animate-none')} {...props}>
		<div className={cn('px-5 pb-5 pt-0', className)}>{children}</div>
	</AccordionPrimitive.Content>);
});
AccordionContent.displayName = AccordionPrimitive.Content.displayName;
